// The case's picture of every wearable it has heard, for the case dashboard's
// Fleet tab. The case serves it with `manage.py dashboard fleet` (sitcase
// docs/fleet.md); one wearable looks like
//
//   { mac, name, fw: "v0.10.0" | null, build, battery: 0-100 | null,
//     power: "charging" | "charged" | "battery" | null, records, advert,
//     where: "case" | "nearby" | "away", rssi, first_seen, last_seen }
//
// `fw` is the internal firmware version. The case doesn't know which version
// is current: we compare with the latest production release from the API.

import { compareVersions } from "./firmwareRelease";

export const LOW_BATTERY = 20;
// The case saves its picture about every 10 s while wearables are near.
export const FLEET_STALE_S = 60;

/** Production releases → Map of internal version → product version. */
export const productVersionsFrom = (releases) =>
  new Map(
    (releases || [])
      .filter((r) => r?.base_version && r?.product_version)
      .map((r) => [r.base_version, r.product_version])
  );

/** The version to show: its product version (v1.4.0) when one matches. */
export const fleetVersionLabel = (fw, productVersions) =>
  (fw && productVersions?.get(fw)) || fw;

/** current | behind | ahead | unknown, against the target internal version. */
export const versionState = (fw, target) => {
  if (!fw || !target) return "unknown";
  const c = compareVersions(fw, target);
  if (c == null) return "unknown";
  return c === 0 ? "current" : c < 0 ? "behind" : "ahead";
};

// Identify (blink white for 5 s, `dashboard identify <mac>`): the firmware
// only takes it on USB with the docked or sync advert up, from v0.11.0.
export const IDENTIFY_FIRMWARE = "v0.11.0";

/** "ready", "old-firmware", or null when the unit isn't docked (no button). */
export const identifyState = (unit, minFirmware = IDENTIFY_FIRMWARE) => {
  if (!unit || unit.where === "away") return null;
  if (!["docked", "sync"].includes(unit.advert)) return null;
  if (!["charging", "charged"].includes(unit.power)) return null;
  const c = compareVersions(unit.fw, minFirmware);
  return c != null && c >= 0 ? "ready" : "old-firmware";
};

// Clearing all records (sync Control {0x09, 0xC1}) needs firmware v0.14.0.
export const CLEAR_FIRMWARE = "v0.14.0";

/** Same as identifyState, for Clear: ready | old-firmware | null. */
export const clearState = (unit) => identifyState(unit, CLEAR_FIRMWARE);

/**
 * How far a clear has got, from the streamed output of `dashboard clear`
 * (sitcase docs/fleet.md): the last `@@PROGRESS {"stage", "pct"}` line, as
 * { stage: "connecting" | "clearing", pct: 0-100 | null }, or null before the
 * first. pct is null on firmware before v0.15.0.
 */
export const clearProgressFrom = (lines) => {
  for (let i = (lines || []).length - 1; i >= 0; i--) {
    const line = lines[i];
    if (!line?.startsWith("@@PROGRESS ")) continue;
    try {
      const p = JSON.parse(line.slice("@@PROGRESS ".length));
      return { stage: p.stage, pct: Number.isFinite(p.pct) ? p.pct : null };
    } catch {
      return null;
    }
  }
  return null;
};

/** The i18n key under caseDashboard.fleet.clear.errors for a failed clear. */
export const clearErrorKey = (error) =>
  ({ "erase-failed": "eraseFailed", refused: "notDocked" }[error] ||
  identifyErrorKey(error));

/** The i18n key under caseDashboard.fleet.identify for a failed result. */
export const identifyErrorKey = (error) =>
  ({
    "not-heard": "notDocked",
    away: "notDocked",
    "not-docked": "notDocked",
    "old-firmware": "oldFirmware",
    "not-found": "notFound",
    busy: "busy",
    timeout: "timeout",
    "app not running": "appDown",
  }[error] || "failed");

export const isLowBattery = (unit) =>
  unit?.battery != null && unit.battery <= LOW_BATTERY;

/** Counts for the tiles, over the units heard now (in the case or nearby). */
export const fleetSummary = (wearables, target) => {
  const units = wearables || [];
  const here = units.filter((u) => u.where !== "away");
  const inCase = here.filter((u) => u.where === "case");
  const states = inCase.map((u) => versionState(u.fw, target));
  return {
    inCase: inCase.length,
    nearby: here.length - inCase.length,
    away: units.length - here.length,
    current: states.filter((s) => s === "current").length,
    // On the latest release or newer (a dev build ahead of it counts).
    upToDate: states.filter((s) => s === "current" || s === "ahead").length,
    behind: states.filter((s) => s === "behind").length,
    lowBattery: inCase.filter(isLowBattery).length,
    charging: inCase.filter((u) => u.power === "charging").length,
    charged: inCase.filter((u) => u.power === "charged").length,
  };
};

// Columns the list can be sorted by, and the direction a first click picks:
// names A→Z, the flattest batteries first, the most records first.
export const FLEET_SORTS = { name: "asc", battery: "asc", records: "desc" };

const compareText = (a, b) => String(a ?? "").localeCompare(String(b ?? ""));
// By the label printed on the case when there is one, then the name.
const byName = (a, b) =>
  (a.label == null) - (b.label == null) ||
  compareText(a.label, b.label) ||
  compareText(a.name, b.name);
const SORT_VALUE = {
  battery: (u) => u.battery,
  records: (u) => u.records || 0,
};

/**
 * Sort by a column ({ key: name | battery | records, dir: asc | desc }).
 * "name" sorts by the case label (`label`, from useWearableLabels) and then
 * the name. Units without a value (battery unknown, no label) go last either
 * way; ties by label and name.
 */
export const sortFleet = (wearables, { key, dir }) => {
  const sign = dir === "desc" ? -1 : 1;
  const value = SORT_VALUE[key];
  return [...(wearables || [])].sort((a, b) => {
    if (!value) {
      // Unlabelled units stay last either way.
      const unlabelled = (a.label == null) - (b.label == null);
      return (
        unlabelled ||
        sign * (compareText(a.label, b.label) || compareText(a.name, b.name))
      );
    }
    const va = value(a);
    const vb = value(b);
    if (va == null || vb == null) {
      return (va == null) - (vb == null) || byName(a, b);
    }
    return sign * (va - vb) || byName(a, b);
  });
};

/**
 * Units grouped by where they are. With a `sort`, each group is in that
 * order; without one, the ones needing attention come first (low battery,
 * then behind on firmware), then by name, and away units most recently
 * heard first.
 */
export const groupFleet = (wearables, target, sort = null) => {
  const rank = (u) =>
    (isLowBattery(u) ? 0 : 2) +
    (versionState(u.fw, target) === "behind" ? 0 : 1);
  const sorted = sort
    ? sortFleet(wearables, sort)
    : [...(wearables || [])].sort((a, b) => rank(a) - rank(b) || byName(a, b));
  const away = sorted.filter((u) => u.where === "away");
  return {
    case: sorted.filter((u) => u.where === "case"),
    nearby: sorted.filter((u) => u.where === "nearby"),
    away: sort
      ? away
      : away.sort((a, b) => (b.last_seen || 0) - (a.last_seen || 0)),
  };
};
